import React from "react";
import { CheckOutlined, FileSearchOutlined, UserOutlined } from "@ant-design/icons";

import { sectionHeading } from "../TreatmentPlans/component/shared/PatientInfoCard";
import ClaimFormUpload from "./ClaimFormUpload";
import PatientPicker from "./PatientPicker";

const SOURCES = [
  {
    key: "find",
    title: "Find or Create Patient",
    sub: "Search your patients, or add one.",
    icon: <UserOutlined />,
  },
  {
    key: "upload",
    title: "Upload as File",
    sub: "A claim form, PDF or photo — we'll read the patient and their insurance.",
    icon: <FileSearchOutlined />,
  },
];

/**
 * The Patient step — two ways to say who the request is for, the same for every
 * request type.
 *
 * "Find or Create Patient" is the search the wizard has always had (with Add new
 * patient). "Upload as File" reads a claim form for the patient, their plan(s) and
 * the submission (ClaimFormUpload). Choosing a patient either way does not move
 * on by itself: the chosen patient is shown, and Continue takes the next step.
 *
 * The two ways sit side by side, both always in view, so switching is one press
 * on the other tile. What the picked way asks for opens full width under them.
 *
 * The host owns which tile is picked and clears the other tile's state when it
 * changes.
 */
export default function PatientStep({
  source,
  onSource,
  patient,
  onSelect,
  officeId,
  claimForm,
  onClaimFormRead,
  onClaimFormReplace,
}) {
  function content(key) {
    if (key === "find") return <PatientPicker selected={patient} onSelect={onSelect} />;
    return (
      <ClaimFormUpload
        officeId={officeId}
        claimForm={claimForm}
        onRead={onClaimFormRead}
        onReplace={onClaimFormReplace}
        selected={patient}
        onSelect={onSelect}
      />
    );
  }

  const picked = SOURCES.find(function (s) { return s.key === source; }) || null;
  function card(s) {
    const isPicked = picked === s;
    return (
      <button
        key={s.key}
        type="button"
        role="radio"
        aria-checked={isPicked}
        aria-controls={isPicked ? "ir-patient-source-" + s.key : undefined}
        className={"ir-choice ir-source-tile" + (isPicked ? " is-selected" : "")}
        onClick={function () { onSource(s.key); }}
      >
        <span className="ir-source-tile-top">
          <span className="ir-choice-icon" aria-hidden>{s.icon}</span>
          <span className="ir-choice-check" aria-hidden>
            {isPicked ? <CheckOutlined /> : null}
          </span>
        </span>
        <span className="ir-choice-title">{s.title}</span>
        <span className="ir-choice-sub">{s.sub}</span>
      </button>
    );
  }

  return (
    <div>
      <div className="ir-step-heading is-first">{sectionHeading("Patient of Interest")}</div>
      <div className="ir-source-tiles" role="radiogroup" aria-label="Patient of Interest">
        {SOURCES.map(card)}
      </div>
      {picked ? (
        <div id={"ir-patient-source-" + picked.key} className="ir-patient-source">
          {content(picked.key)}
        </div>
      ) : null}

      {source ? null : (
        <p className="ir-hint" style={{ textAlign: "center", marginTop: 16 }}>
          Choose how to find the patient.
        </p>
      )}
    </div>
  );
}
